import React, { useRef, useState } from 'react';
import { type ApolloQueryResult } from '@apollo/react-hooks';
import { Dialog } from '@mui/material';
import { Cancel, ReplayCircleFilled, SwipeLeft } from '@mui/icons-material';
import { ManageChiefSupervisorDialog } from './ManageChiefSupervisorDialog';
import { FUNCTION_HUB_TEXT } from '../lang';
import { Alert, type AlertDetails } from '../../../../../../components';
import {
  ApplicationViewList,
  type ApplicationViewListProps,
  type ApplicationViewTableDataItem,
  type CustomAction,
  type FilterFields,
  type TableColumns,
} from '../../../../../Application/components';
import { type RawFundingWithStatus } from '../../../../../Application/components/ApplicationViewList/graphql';
import { type ApplicationViewTableFilterFields } from '../../../../../Application/components/ApplicationViewList/types';

export enum FUNCTION_MODE {
  DECLINE_APPLICATION = 1,
  CHANGE_SUPERVISOR,
  REJECT_APPLICATION,
}

type ChangeSupervisorProps = {
  filter: any;
  functionMode: FUNCTION_MODE;
  departmentLevel: boolean;
};

export const ManageChiefSupervisor: React.FC<ChangeSupervisorProps> = (
  props: ChangeSupervisorProps,
) => {
  const { filter: listFilter, functionMode, departmentLevel } = props;

  const [dialogControl, setDialogControl] = useState<{
    dialogOpen: boolean;
    applicationId: string;
    nominationId: string;
    fundingDetails: RawFundingWithStatus[];
  }>({
    dialogOpen: false,
    applicationId: '',
    nominationId: '',
    fundingDetails: [],
  });
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  // default value
  const getRefetchRef = useRef<() => Promise<ApolloQueryResult<any>>>(
    async () => ({
      data: {},
      loading: false,
      networkStatus: 7,
      stale: false,
    }),
  );
  const hiddenFilters: FilterFields[] = React.useMemo(
    () => [
      ...(departmentLevel
        ? ['department' as keyof ApplicationViewTableFilterFields]
        : []),
      'partnerCategory',
      'applicantIdentityType',
      'applicantLocalIndicator',
      'gsAssignee',
    ],
    [],
  );

  const visibleColumns: TableColumns[] = React.useMemo(
    () => [
      'applicationNo',
      'applicantName',
      'entryYear',
      'applyProgramme',
      'researchAreas',
      'scholarshipApplications',
      'hkpfsDisplayInformation',
      'nominatedFundings',
      'applicationStatus',
    ],
    [],
  );

  const handleCloseDialog = (): void => {
    setDialogControl({
      dialogOpen: false,
      applicationId: '',
      nominationId: '',
      fundingDetails: [],
    });
  };

  const handleOpenAlert = (alertDetails: AlertDetails): void => {
    setOpenAlert(true);
    setAlertDetails(alertDetails);
    if (alertDetails.alertType === 'success') handleCloseDialog();
  };

  const { dialogOpen, applicationId, nominationId, fundingDetails } =
    dialogControl;

  const isValidOpenDialog = (): boolean => {
    switch (functionMode) {
      case FUNCTION_MODE.CHANGE_SUPERVISOR:
        return dialogOpen && !!applicationId && !!nominationId;

      // following 2 case don't require a nomination id
      case FUNCTION_MODE.DECLINE_APPLICATION:
      case FUNCTION_MODE.REJECT_APPLICATION:
        return dialogOpen && !!applicationId;
      default:
        return false;
    }
  };
  const validDialogControl: boolean = isValidOpenDialog();

  const dialogMap: Record<
    FUNCTION_MODE,
    { toolTip: string; actionIcon: React.ReactNode }
  > = {
    [FUNCTION_MODE.CHANGE_SUPERVISOR]: {
      toolTip: FUNCTION_HUB_TEXT.CHANGE_SUPERVISOR.CHANGE_CHIEF,
      actionIcon: <ReplayCircleFilled />,
    },
    [FUNCTION_MODE.DECLINE_APPLICATION]: {
      toolTip: FUNCTION_HUB_TEXT.DECLINE_APPLICATION.EXPLANATION,
      actionIcon: <Cancel />,
    },
    [FUNCTION_MODE.REJECT_APPLICATION]: {
      toolTip: FUNCTION_HUB_TEXT.REJECT_APPLICATION.EXPLANATION,
      actionIcon: <SwipeLeft />,
    },
  };

  const handleOpenDialog = (
    row: ApplicationViewTableDataItem,
    refetch: () => Promise<ApolloQueryResult<any>>,
  ): void => {
    const {
      applicationId,
      nominationId,
      rawFundingWithStatus: fundingDetails,
    } = row;

    setDialogControl({
      dialogOpen: true,
      applicationId,
      nominationId,
      fundingDetails,
    });

    // Ref is acceptable here. Because currently update ref is triggered together with setState, the re-render must be triggered.
    // But as update ref.current will not trigger re-render itself,
    // there is a risk (if remove setDialogControl here) that after refetch updated, the latest value is not passed to component.
    getRefetchRef.current = refetch;
  };

  const customActions: CustomAction[] = [
    {
      icon: dialogMap[functionMode].actionIcon,
      title: dialogMap[functionMode].toolTip,
      handleAction: handleOpenDialog,
      checkDisable: () => false,
    },
  ];
  const filterProps: ApplicationViewListProps['filterProps'] = {
    hiddenFilterFields: hiddenFilters,
  };
  const tableProps: ApplicationViewListProps['tableProps'] = {
    initialVisibleColumns: visibleColumns,
    listFilter,
    customActions,
    pickCommonActions: [],
  };

  return (
    <>
      <div>
        <Alert
          openAlert={openAlert}
          setOpenAlert={setOpenAlert}
          alertDetails={alertDetails}
        />
        <ApplicationViewList
          tableProps={tableProps}
          filterProps={filterProps}
        />
      </div>

      <Dialog
        open={validDialogControl}
        onClose={handleCloseDialog}
        maxWidth="md"
        scroll={'body'}
      >
        {validDialogControl && (
          <ManageChiefSupervisorDialog
            applicationId={applicationId}
            nominationId={nominationId}
            functionMode={functionMode}
            fundingDetails={fundingDetails}
            departmentLevel={departmentLevel}
            handleCloseDialog={handleCloseDialog}
            handleOpenAlert={handleOpenAlert}
            refetch={getRefetchRef.current}
          />
        )}
      </Dialog>
    </>
  );
};
